<template>
  <div class="px-4 pt-3">
    <!-- 상단 — 게임 선택 + 신규 라운드 버튼 -->
    <v-card outlined class="mb-3">
      <v-card-title class="pa-3">
        <v-icon left color="cyan">mdi-clipboard-edit-outline</v-icon>
        <span class="text-h6">수동 운영 라운드 관리</span>
        <v-spacer></v-spacer>
        <v-select
          v-model="selectedGameId"
          :items="manualGames"
          item-text="label"
          item-value="game_id"
          label="manual 게임 선택"
          outlined dense hide-details
          class="mr-2"
          style="max-width:380px"
          :loading="gamesLoading"
          @change="onSelectGame"
        ></v-select>
        <v-btn small color="primary" depressed :disabled="!selectedGameId" @click="openCreateDialog">
          <v-icon left small>mdi-plus</v-icon>신규 라운드
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- 라운드 목록 -->
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="rounds"
        :items-per-page="20"
        :loading="roundsLoading"
        no-data-text="라운드가 없습니다. '신규 라운드' 버튼으로 등록하세요."
        dense
        class="elevation-0"
      >
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
        </template>
        <template v-slot:item.title="{ item }">
          <strong>{{ (item.config && item.config.title) || '-' }}</strong>
          <div v-if="item.config && item.config.description" class="caption grey--text">{{ item.config.description }}</div>
        </template>
        <template v-slot:item.choices="{ item }">
          <div class="d-flex flex-wrap" style="gap:3px">
            <v-chip v-for="c in (item.choices_snapshot || [])" :key="c.code" x-small label outlined>
              <strong class="mr-1">{{ c.code }}</strong>
              <span class="grey--text">{{ c.label }}</span>
            </v-chip>
          </div>
        </template>
        <template v-slot:item.result_direction="{ item }">
          <v-chip v-if="item.result_direction" x-small color="green" dark>{{ item.result_direction }}</v-chip>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:item.started_at="{ item }">
          <span class="caption">{{ fmtDateTime(item.started_at) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn x-small color="orange" outlined class="mr-1"
            v-if="item.status === 'open'"
            @click="onCloseRound(item)">
            <v-icon left x-small>mdi-lock-clock</v-icon>마감
          </v-btn>
          <v-btn x-small color="green" outlined class="mr-1"
            v-if="item.status === 'open' || item.status === 'closed'"
            @click="openResultDialog(item)">
            <v-icon left x-small>mdi-check-decagram-outline</v-icon>결과
          </v-btn>
          <v-btn x-small color="red" outlined
            v-if="item.status === 'open' || item.status === 'closed'"
            @click="onCancelRound(item)">
            <v-icon left x-small>mdi-close-circle-outline</v-icon>취소
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- 신규 라운드 등록 다이얼로그 -->
    <v-dialog v-model="createDialog" max-width="640" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left color="cyan">mdi-plus-circle-outline</v-icon>
          신규 라운드 등록
          <v-spacer></v-spacer>
          <v-btn icon @click="createDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="createForm.title"
            label="주제 *"
            outlined dense hide-details class="mb-3"
            placeholder="이번 대통령은? / 오늘 우승팀? / 다음 분기 매출 N억 이상?"
          ></v-text-field>
          <v-textarea
            v-model="createForm.description"
            label="설명 (선택)"
            outlined dense hide-details class="mb-3" rows="2"
            placeholder="베팅 참여자가 볼 추가 설명"
          ></v-textarea>
          <v-select
            v-model="createForm.choice_group_id"
            :items="choiceGroups"
            item-text="label"
            item-value="group_id"
            label="선택지 그룹 *"
            outlined dense hide-details class="mb-3"
            :loading="choiceGroupsLoading"
            hint="없으면 '선택지 그룹 관리' 페이지에서 먼저 등록"
            persistent-hint
          ></v-select>
          <!-- 베팅 참여 시작 — HTML5 datetime-local 위젯 (브라우저 native calendar + clock) -->
          <v-text-field
            v-model="createForm.bet_open_at"
            label="베팅 참여 시작 시각 (선택)"
            type="datetime-local"
            outlined dense hide-details class="mb-3"
            prepend-inner-icon="mdi-calendar-clock"
            persistent-hint
            hint="비워두면 등록 즉시 시작. 미래 시각이면 사용자는 그 시각 전까지 베팅 불가."
            clearable
            @click:clear="createForm.bet_open_at = ''"
          ></v-text-field>
          <!-- 베팅 참여 종료 예정 (UI 안내용) -->
          <v-text-field
            v-model="createForm.bet_close_at"
            label="베팅 참여 종료 예정 시각 (선택, 안내용)"
            type="datetime-local"
            outlined dense hide-details class="mb-2"
            prepend-inner-icon="mdi-calendar-clock-outline"
            persistent-hint
            hint="UI 안내용. 실제 마감은 [마감] 버튼 — 운영자가 직접."
            clearable
            @click:clear="createForm.bet_close_at = ''"
          ></v-text-field>
          <v-alert v-if="createError" dense text type="error" class="mt-3">{{ createError }}</v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false">취소</v-btn>
          <v-btn color="primary" depressed :loading="creating" @click="submitCreate">등록</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 결과 입력 다이얼로그 -->
    <v-dialog v-model="resultDialog" max-width="540" scrollable>
      <v-card v-if="resultTarget">
        <v-card-title>
          <v-icon left color="green">mdi-check-decagram</v-icon>
          결과 입력
          <v-spacer></v-spacer>
          <v-btn icon @click="resultDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div class="text-subtitle-2 mb-2">
            {{ (resultTarget.config && resultTarget.config.title) || ('Round #' + resultTarget.round_number) }}
          </div>
          <v-alert dense text type="info" class="mb-3 body-2">
            아래 선택지 중 <strong>승리(정답)</strong> 를 클릭하세요. 즉시 정산되며 환불은 [취소] 버튼으로.
          </v-alert>
          <v-radio-group v-model="resultForm.choice_code" hide-details class="mt-0">
            <v-radio v-for="c in (resultTarget.choices_snapshot || [])"
              :key="c.code"
              :value="c.code"
              :label="`${c.label} (${c.code})`"
              class="mb-1"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-model="resultForm.reason"
            label="메모 (선택 — 감사 추적용)"
            outlined dense hide-details class="mb-2 mt-4"
          ></v-text-field>
          <v-alert v-if="resultError" dense text type="error" class="mt-3">{{ resultError }}</v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="resultDialog = false">취소</v-btn>
          <v-btn color="green" depressed dark
            :disabled="!resultForm.choice_code"
            :loading="resulting"
            @click="submitResult">
            결과 확정 + 정산
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'Danta_ManualRoundManage',
  data: () => ({
    gamesLoading: false,
    roundsLoading: false,
    choiceGroupsLoading: false,
    creating: false,
    resulting: false,

    manualGames: [],
    selectedGameId: null,
    rounds: [],
    choiceGroups: [],

    headers: [
      { text: '#', value: 'round_number', width: '70px' },
      { text: '상태', value: 'status', width: '90px' },
      { text: '주제', value: 'title' },
      { text: '선택지', value: 'choices', sortable: false },
      { text: '결과', value: 'result_direction', width: '100px' },
      { text: '등록', value: 'started_at', width: '140px' },
      { text: '액션', value: 'actions', width: '260px', sortable: false, align: 'right' },
    ],

    createDialog: false,
    createError: '',
    createForm: {
      title: '',
      description: '',
      choice_group_id: null,
      bet_open_at: '',
      bet_close_at: '',
    },

    resultDialog: false,
    resultError: '',
    resultTarget: null,
    resultForm: { choice_code: '', reason: '' },
  }),
  created() {
    this.loadGames();
    this.loadChoiceGroups();
  },
  methods: {
    ...mapActions([
      'DANTA_GET_GAME_LIST',
      'DANTA_GET_CHOICE_GROUPS',
      'DANTA_MANUAL_ROUND_CREATE',
      'DANTA_MANUAL_ROUND_CLOSE',
      'DANTA_MANUAL_ROUND_RESULT',
      'DANTA_MANUAL_ROUND_CANCEL',
      'DANTA_MANUAL_ROUNDS_LIST',
    ]),

    async loadGames() {
      this.gamesLoading = true;
      try {
        // store/actions.js 의 DANTA_GET_GAME_LIST 가 destructure { status, template_id, ... } 패턴이라
        // 인자 없이 호출 시 TypeError. 빈 객체 + page_size 크게 (50개 넘으면 33번이 다른 페이지에 있을 수도).
        const r = await this.DANTA_GET_GAME_LIST({ page: 1, page_size: 200 });
        const list = r?.game_list || r?.games || [];
        // 매칭 조건:
        //   - schedule_type='manual' (정상 등록)
        //   - 또는 template.game_mode='manual' (schedule_type 이 잘못 박힌 옛 게임도 포함)
        //   - 또는 config.manual===true (다른 경로로 박힌 경우 fallback)
        this.manualGames = list
          .filter(g =>
            g.schedule_type === 'manual'
            || g.game_mode === 'manual'
            || (g.config && g.config.manual === true)
          )
          .map(g => ({
            ...g,
            label: `[${g.game_id}] ${g.title} (status=${g.status}${g.schedule_type !== 'manual' ? ', ⚠️ schedule_type=' + g.schedule_type : ''})`,
          }));
      } catch (e) {
        console.error('manual games 로드 실패:', e);
      } finally {
        this.gamesLoading = false;
      }
    },

    async loadChoiceGroups() {
      this.choiceGroupsLoading = true;
      try {
        const r = await this.DANTA_GET_CHOICE_GROUPS({});
        this.choiceGroups = (r?.groups || []).map(g => ({
          ...g,
          label: `${g.group_name} (${g.group_code}) — ${(g.choices || []).map(c => c.code).join('/')}`,
        }));
      } catch (e) {
        console.error('choice_groups 로드 실패:', e);
      } finally {
        this.choiceGroupsLoading = false;
      }
    },

    async loadRounds(gameId) {
      if (!gameId) { this.rounds = []; return; }
      this.roundsLoading = true;
      try {
        const r = await this.DANTA_MANUAL_ROUNDS_LIST({ game_id: gameId, limit: 100 });
        this.rounds = r?.rounds || [];
      } catch (e) {
        console.error('rounds 로드 실패:', e);
      } finally {
        this.roundsLoading = false;
      }
    },

    onSelectGame(gameId) {
      this.selectedGameId = gameId;
      this.loadRounds(gameId);
    },

    openCreateDialog() {
      this.createForm = { title: '', description: '', choice_group_id: null, bet_open_at: '', bet_close_at: '' };
      this.createError = '';
      this.createDialog = true;
    },

    async submitCreate() {
      if (!this.createForm.title)            { this.createError = '주제 필수'; return; }
      if (!this.createForm.choice_group_id)  { this.createError = '선택지 그룹 필수'; return; }
      this.createError = '';
      this.creating = true;
      try {
        await this.DANTA_MANUAL_ROUND_CREATE({
          game_id:        this.selectedGameId,
          title:          this.createForm.title.trim(),
          description:    this.createForm.description || '',
          choice_group_id: this.createForm.choice_group_id,
          bet_open_at:    this.createForm.bet_open_at  || null,
          bet_close_at:   this.createForm.bet_close_at || null,
        });
        this.createDialog = false;
        await this.loadRounds(this.selectedGameId);
      } catch (e) {
        this.createError = e?.response?.data?.err_message || e?.message || '등록 실패';
      } finally {
        this.creating = false;
      }
    },

    async onCloseRound(item) {
      if (!confirm(`라운드 #${item.round_number} 베팅을 마감하시겠습니까?`)) return;
      try {
        await this.DANTA_MANUAL_ROUND_CLOSE({ round_id: item.round_id });
        await this.loadRounds(this.selectedGameId);
      } catch (e) {
        alert('마감 실패: ' + (e?.message || e));
      }
    },

    openResultDialog(item) {
      this.resultTarget = item;
      this.resultForm = { choice_code: '', reason: '' };
      this.resultError = '';
      this.resultDialog = true;
    },

    async submitResult() {
      if (!this.resultForm.choice_code) { this.resultError = '승리 선택지를 선택하세요'; return; }
      this.resulting = true;
      try {
        await this.DANTA_MANUAL_ROUND_RESULT({
          round_id:    this.resultTarget.round_id,
          choice_code: this.resultForm.choice_code,
          result_meta: {},
          reason:      this.resultForm.reason || '',
        });
        this.resultDialog = false;
        await this.loadRounds(this.selectedGameId);
      } catch (e) {
        this.resultError = e?.response?.data?.err_message || e?.message || '결과 입력 실패';
      } finally {
        this.resulting = false;
      }
    },

    async onCancelRound(item) {
      const reason = window.prompt(`라운드 #${item.round_number} 취소 — 전원 환불됩니다. 사유를 입력하세요:`);
      if (reason == null) return;
      try {
        await this.DANTA_MANUAL_ROUND_CANCEL({ round_id: item.round_id, reason: reason || 'admin_revert' });
        await this.loadRounds(this.selectedGameId);
      } catch (e) {
        alert('취소 실패: ' + (e?.message || e));
      }
    },

    statusLabel(s) {
      return ({ open: '진행중', closed: '마감', settled: '정산완료', cancelled: '취소' })[s] || s || '-';
    },
    statusColor(s) {
      return ({ open: 'cyan', closed: 'orange', settled: 'green', cancelled: 'grey' })[s] || 'grey';
    },
    fmtDateTime(iso) {
      if (!iso) return '-';
      try { return new Date(iso).toLocaleString('ko-KR'); } catch (_) { return iso; }
    },
  },
};
</script>
